import { Badge, Box, Flex, InlineStack, PlainButton, Spinner, Stack, Text } from '@customerio/pluma-components/react';
import type { PaymentCard } from '@/services/billing/types';
import type { CreditsPurchaseFormView } from '../hooks/use-credits-purchase-form';
import { creditsPurchaseCardView, STRIPE_LOAD_ERROR_MESSAGE } from '../utils/credits-purchase-form-helpers';
import { AddFundsBillingFields } from './add-funds-billing-fields';
import { CardFields } from './card-fields';
import * as styles from './add-funds.css';

const BRAND_LABELS = new Map([
  ['visa', 'Visa'],
  ['mastercard', 'Mastercard'],
  ['amex', 'American Express'],
  ['discover', 'Discover'],
]);

export function cardLabel(card: PaymentCard | null): string {
  if (card == null) return 'Card on file';
  return `${BRAND_LABELS.get(card.brand) ?? 'Card'} ending in ${card.last4}`;
}

export type CardEntry = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
};

type AddFundsCardSectionProps = {
  form: CreditsPurchaseFormView;
  cardEntry: CardEntry;
  cardError?: string;
};

export function AddFundsCardSection({ form, cardEntry, cardError }: AddFundsCardSectionProps) {
  const view = creditsPurchaseCardView({
    isStripeLoadError: form.stripeLoad.isError,
    isStripeLoading: form.stripeLoad.isLoading,
    useSavedCard: form.useSavedCard,
  });

  switch (view) {
    case 'stripe-error':
      return (
        <Text color="critical" data-test-credits-stripe-load-error>
          {STRIPE_LOAD_ERROR_MESSAGE}
        </Text>
      );
    case 'saved-card':
      return <SavedCardRow form={form} cardEntry={cardEntry} />;
    case 'stripe-loading':
      return null;
    case 'new-card':
      return <NewCardFields form={form} cardEntry={cardEntry} cardError={cardError} />;
    default: {
      const _exhaustive: never = view;
      return _exhaustive;
    }
  }
}

type SavedCardRowProps = Omit<AddFundsCardSectionProps, 'cardError'>;

function SavedCardRow({ form, cardEntry }: SavedCardRowProps) {
  const { savedCard } = form;

  if (savedCard.isCardLoading) {
    return (
      <InlineStack alignBlock="center" gap="050">
        <Spinner />
        <Text size="sm">Loading payment method…</Text>
      </InlineStack>
    );
  }
  return (
    <Flex
      data-test-add-funds-saved-card
      className={styles.optionBox}
      alignItems="center"
      justifyContent="space-between"
      gap="200"
    >
      {savedCard.isCardError ? (
        <Text color="critical">Unable to load your card details. Refresh the page or use a new card.</Text>
      ) : (
        <Text className="fs-mask">{cardLabel(savedCard.cardDetails)}</Text>
      )}
      <PlainButton
        isDisabled={form.isSubmitting}
        onClick={() => {
          form.onTogglePaymentMethod();
          form.onSaveCardChange(true);
          cardEntry.onOpen();
        }}
      >
        <Text color="accent" weight="bold" size="sm">
          Change
        </Text>
      </PlainButton>
    </Flex>
  );
}

export function CardBrands() {
  return (
    <InlineStack gap="050" alignBlock="center" data-test-add-funds-card-brands>
      <Badge color="neutral" variant="outline">
        VISA
      </Badge>
      <Badge color="neutral" variant="outline">
        MC
      </Badge>
      <Badge color="neutral" variant="outline">
        AMEX
      </Badge>
    </InlineStack>
  );
}

function NewCardFields({ form, cardEntry, cardError }: AddFundsCardSectionProps) {
  return (
    <Stack gap="150" data-test-add-funds-new-card>
      <CardFields
        onChange={(isComplete) => {
          cardEntry.onOpen();
          form.setIsCardComplete(isComplete);
        }}
        isDetailsHidden={!cardEntry.isOpen}
        onNumberFocus={cardEntry.onOpen}
        numberLabelAccessory={<CardBrands />}
      />
      {cardError == null ? null : (
        <Text color="critical" size="sm" data-test-add-funds-card-error>
          {cardError}
        </Text>
      )}
      {cardEntry.isOpen ? <AddFundsBillingFields form={form} /> : null}
      {form.hasSavedCard ? (
        <Box>
          <PlainButton
            onClick={() => {
              form.onTogglePaymentMethod();
              cardEntry.onClose();
            }}
          >
            <Text color="accent" weight="bold" size="sm">
              Use saved card
            </Text>
          </PlainButton>
        </Box>
      ) : null}
    </Stack>
  );
}
